import { useNavigate } from "react-router-dom";
import RMLogo from "@/assets/images/rm-logo-emblem.svg";
import { Typography } from "@/components/atoms";
import { useLayout, useOperator, useOperatorAccess, useSubscriptionAssignment } from "@/hooks";
import { useGetProviderSubscriptionsQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { useAppSelector } from "@/redux/hooks";
import { operatorsSelector } from "@/redux/slices/operator/selectors";
import { clsx } from "@/utils";

// All backend-accepted statuses, so the "most recent assignment" query also surfaces ended/cancelled
// plans (note: the API does not accept "expired" as a filter value).
const ALL_SUBSCRIPTION_STATUSES = ["active", "pending_cancellation", "trial", "cancelled", "suspended", "overdue"] as const;

interface SidebarLogoProps extends React.HTMLAttributes<HTMLDivElement> {
  ignoreSidebarOpen?: boolean;
  inlineLayout?: boolean;
}

export const SidebarLogo = ({ ignoreSidebarOpen, inlineLayout = false, className, ...props }: SidebarLogoProps) => {
  const navigate = useNavigate();
  const {
    layout: { sidebarOpen: isOpen },
  } = useLayout();
  const sidebarOpen = ignoreSidebarOpen ? true : isOpen;
  const operator = useOperator();
  const operators = useAppSelector(operatorsSelector);
  const logoUrl = operator?.images?.emblem ? operator.images.emblem : RMLogo;
  const { subscriptionStatus } = useSubscriptionAssignment();
  const { isUnsubscribed } = useOperatorAccess();

  // Most recent assignment across all statuses. Used as the plan-name source so current-but-not-
  // strictly-active plans (e.g. "overdue") still display, and to show the last tier when unsubscribed.
  const { data: latestSubscriptions } = useGetProviderSubscriptionsQuery(
    { providerId: operator.id, status: [...ALL_SUBSCRIPTION_STATUSES], limit: 1, orderBy: ["startDate:DESC"] },
    { skip: !operator.id }
  );
  const latestSubscription = latestSubscriptions?.[0]?.subscription;

  const handleClick = () => navigate(`/operator/${operator.id}/operators`);

  // Prefer the active assignment; fall back to the latest one so overdue/past-due plans still show.
  const planName = subscriptionStatus.baseSubscription?.subscription.name ?? latestSubscription?.name;
  const planIconSvg = subscriptionStatus.baseSubscription?.subscription.iconSvg ?? latestSubscription?.iconSvg;

  return (
    <div className={clsx(`${sidebarOpen ? "px-3" : "justify-center px-2.5"}`, className)} {...props}>
      <figure
        {...(operators.length > 1 && { onClick: handleClick })}
        className={clsx("flex flex-shrink-0 items-center rounded-lg py-5 sm:px-2.5", {
          "cursor-pointer hover:bg-primary-light": operators.length > 1,
          "place-content-center": !sidebarOpen,
        })}
      >
        <img className="h-8 w-8 flex-shrink-0 object-contain" src={logoUrl} alt="RideMinder" />
        {sidebarOpen && (
          <div
            className={clsx("ml-3 flex", {
              "flex-col": !inlineLayout,
              "items-center gap-2": inlineLayout,
            })}
          >
            <Typography variant="h3">{operator.name || "Rideminder"}</Typography>
            {isUnsubscribed ? (
              <Typography variant="small-regular" className="text-neutral-dark-gray">
                {latestSubscription?.name ? `${latestSubscription.name} (Unsubscribed)` : "(Unsubscribed)"}
              </Typography>
            ) : (
              planName && (
                <div className="flex items-center gap-1">
                  {planIconSvg && (
                    <div className="h-4 w-4 flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4" dangerouslySetInnerHTML={{ __html: planIconSvg }} />
                  )}
                  <Typography variant="small-regular" className="text-neutral-dark-gray">
                    {planName}
                  </Typography>
                </div>
              )
            )}
          </div>
        )}
      </figure>
    </div>
  );
};
